import { OnlineOrderFilterForm } from 'views/OnlineStore/hooks/useOnlineOrder'
import httpRequest from 'httpRequest'
import {
    OnlineOrderLookupContract,
    OrderContract,
    OrderDetailContract,
    OrderLineKdsStatus,
    OrderLineLookupContract,
    OrderLineStatus,
    OrderLookupContract,
    OrderOwnerContract,
    ProcessPaymentContract,
    UnreadOrder,
    VoidOrderLineRequestContract,
    VoidOrderRequestContract,
} from 'models'
import { searchOrderRequest } from 'store/actions/orderLookup.action'
import { CountModel } from 'models/CountModel'
import { ProcessSaleContract } from 'models/ProcessSaleContract'

const ORDER_PREFIX = '/location/orders'

export const toVoidOrderRequest = (
    order: OrderContract,
    pinCode: string | undefined | null,
): VoidOrderRequestContract => {
    if (!order.voidComp) throw new Error('no defined void details')

    return {
        orderID: order.orderID!,
        voidReason: {
            reasonID: order.voidComp.voidCompReasonID!,
            reason: order.voidComp.name,
            note: order.voidComp.note,
        },
        pinCode: pinCode,
        isRestockInventory: true, // Always set to true for now. Later we can add a UI control for the user to chosse whether the order's items should be restocked or not.
    }
}

export interface OrderLineSearchRequest {
    tableGroupID?: string
    tableIDs?: string[]
    status?: OrderLineStatus
    kdsStatus?: OrderLineKdsStatus
    orderIDs?: string[]
    isParentLine?: boolean
    includeOrder?: boolean
}

const orderService = {
    searchOrders(searchRequest: searchOrderRequest) {
        return httpRequest.get<OrderLookupContract[]>(`${ORDER_PREFIX}`, {
            params: searchRequest,
        })
    },
    getOrder(orderID: string) {
        return httpRequest.get<OrderDetailContract>(
            `${ORDER_PREFIX}/${orderID}`,
        )
    },
    voidOrder(order: VoidOrderRequestContract) {
        return httpRequest.post<OrderContract>(`${ORDER_PREFIX}/void`, order)
    },
    voidOrderLines(
        orderID: string,
        voidContract: VoidOrderLineRequestContract,
    ) {
        return httpRequest.post<OrderContract>(
            `${ORDER_PREFIX}/${orderID}/void-order-lines`,
            voidContract,
        )
    },
    processOrder(order: OrderContract) {
        return httpRequest.post<ProcessSaleContract>(ORDER_PREFIX, order)
    },
    processAndSendOrder(order: OrderContract) {
        return httpRequest.post<ProcessSaleContract>(
            `${ORDER_PREFIX}/send`,
            order,
        )
    },
    sendOrder: (orderID: string) =>
        httpRequest.post<OrderContract>(`${ORDER_PREFIX}/${orderID}/send`),
    getPendingOrders: (orderIDs: string[]) =>
        httpRequest.get<OrderLookupContract[]>(
            `${ORDER_PREFIX}/pending-orders`,
            {
                params: { orderIDs },
            },
        ),
    finalizeOrder: (orderID: string) =>
        httpRequest.post(`${ORDER_PREFIX}/${orderID}/finalize`),
    finalizeOrders: (orderIDs: string[]) =>
        httpRequest.post(`${ORDER_PREFIX}/finalize`, orderIDs),
    completePickupOrDelivery: (orderID: string) =>
        httpRequest.post(
            `${ORDER_PREFIX}/${orderID}/complete-pickup-or-delivery`,
        ),
    syncOfflineOrder: (
        order: OrderContract,
        payments: ProcessPaymentContract[] | null,
    ) => httpRequest.post(`${ORDER_PREFIX}/offline-sync`, { order, payments }),
    // Table Service APIs
    getTableOrders: (tableID: string, includeAllSibling: boolean = false) =>
        httpRequest.get<OrderOwnerContract[]>(
            `${ORDER_PREFIX}/by-table/${tableID}`,
            { params: { includeAllSibling } },
        ),

    getTableGroupOrders: (tableGroupID: string) =>
        httpRequest.get<OrderOwnerContract[]>(
            `${ORDER_PREFIX}/by-table-group/${tableGroupID}`,
        ),
    getOrderlines: (searchRequest: OrderLineSearchRequest) =>
        httpRequest.get<OrderLineLookupContract[]>(
            `${ORDER_PREFIX}/order-lines`,
            {
                params: searchRequest,
            },
        ),
    // get online orders
    getOnlineOrders: (searchRequest: OnlineOrderFilterForm) =>
        httpRequest.get<OnlineOrderLookupContract[]>(
            `${ORDER_PREFIX}/online-orders`,
            {
                params: {
                    customerName: searchRequest.customerName,
                    brandID: searchRequest.brandID,
                    startDate: searchRequest.dateRange[0]?.toISOString(),
                    endDate: searchRequest.dateRange[1]?.toISOString(),
                    onlineOrderChannelIDs: searchRequest.onlineOrderChannelIDs,
                    onlineStoreIDs: searchRequest.onlineStoreIDs,
                },
            },
        ),
    getOnlineOrderByID: (orderID: string) =>
        httpRequest.get<OnlineOrderLookupContract>(
            `${ORDER_PREFIX}/online-order-by-id/${orderID}`,
        ),
    getOnlineOrderByOrderNo: (orderNo: string) =>
        httpRequest.get<OnlineOrderLookupContract>(
            `${ORDER_PREFIX}/online-order-by-order-no/${orderNo}`,
        ),
    // online order notification
    getUnreadOrders: () =>
        httpRequest.get<UnreadOrder[]>(`${ORDER_PREFIX}/unread`),
    getUnreadOrderCount: () =>
        httpRequest.get<CountModel>(`${ORDER_PREFIX}/unread-count`),
    markOrderAsRead: (orderID: string) =>
        httpRequest.put(`${ORDER_PREFIX}/mark-read/${orderID}`),
    reprintOrderLine: (orderLineIDs: string[]) =>
        httpRequest.put<void>(`${ORDER_PREFIX}/re-print`, orderLineIDs),
}

export default orderService
